/*
 * Title and text rules for the Index Book (DESIGN-SPEC §1.12). Every screen
 * that files, sorts or shortens a title goes through these so the index, the
 * running head, the rail and Look up agree character for character.
 */

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const ORDINAL = /^\d+(?:\.\d+)*(?:[.)]\s+|\s+·\s+)/;
const SHORT_SPLIT = / — | – | - |: /;
const SPLIT_TITLE = /^(.{8,}?)(?: — | – | - |: )(.{6,})$/;

export const stripOrdinal = (title: string): string => title.replace(ORDINAL, '');

/** The first part of a title when it is long enough to stand alone. */
export function shortTitle(title: string): string {
  const bare = stripOrdinal(title);
  const first = bare.split(SHORT_SPLIT)[0] ?? bare;
  return first.length >= 16 && first !== bare ? first : bare;
}

/**
 * Short titles for a set of documents. Two documents that would share a short
 * title both fall back to their full title so neither is ambiguous.
 */
export function shortTitles<T extends { id: string; title: string }>(docs: readonly T[]): Map<string, string> {
  const byShort = new Map<string, T[]>();
  for (const d of docs) {
    const s = shortTitle(d.title);
    const list = byShort.get(s);
    if (list) list.push(d);
    else byShort.set(s, [d]);
  }
  const out = new Map<string, string>();
  for (const [s, list] of byShort) for (const d of list) out.set(d.id, list.length > 1 ? d.title : s);
  return out;
}

/** `[main, rest]`; rest is the italic sub-line or stand-first, `''` when the title does not split. */
export function splitTitle(title: string): [string, string] {
  const m = title.match(SPLIT_TITLE);
  return m ? [m[1]!, m[2]!] : [title, ''];
}

export const sortKey = (s: string): string => stripOrdinal(s).replace(/^[^A-Za-z0-9]+/, '').toLowerCase();

/** A–Z by the sort key; anything else files under `#`. */
export function letterOf(s: string): string {
  const c = (sortKey(s)[0] ?? '#').toUpperCase();
  return /[A-Z]/.test(c) ? c : '#';
}

/** Letter order with `#` always last. */
export const compareLetters = (a: string, b: string): number => Number(a === '#') - Number(b === '#') || a.localeCompare(b);

/** Numbered series stay in series order: `2. …` before `10. …`. */
export const naturalCompare = (a: string, b: string): number => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });

export type TitleSize = '' | 'l' | 'xl';

/** Longer titles step down: over 72 characters `l`, over 110 `xl`. */
export const titleSize = (title: string): TitleSize => (title.length > 110 ? 'xl' : title.length > 72 ? 'l' : '');

/** Strips Markdown links, emphasis, code ticks and block markers, then collapses whitespace. */
export function cleanExcerpt(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[\s(])[*_](\S(?:.*?\S)?)[*_](?=[\s).,;:!?]|$)/g, '$1$2')
    .replace(/`+/g, '')
    .replace(/^\s*(#{1,6}|>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Cuts at the last word boundary before `max` characters and ends with an ellipsis. */
export function cutAtWord(s: string, max: number): string {
  if (s.length <= max) return s;
  const head = s.slice(0, max);
  const at = head.lastIndexOf(' ');
  return `${(at > 0 ? head.slice(0, at) : head).replace(/[\s,;:.–—-]+$/, '')}…`;
}

/** Anchor slug for index entries (`/index?at=<slug>`): lowercase, runs of non-alphanumerics become `-`. */
export const slug = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'x';
